"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import { cn } from "@/lib/utils"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "@/components/ui/carousel"

interface ProductImageCarouselProps {
  images: string[]
  productName: string
}

export function ProductImageCarousel({ images, productName }: ProductImageCarouselProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [api, setApi] = useState<CarouselApi>()
  const hasMultiple = images.length > 1

  useEffect(() => {
    if (!api) return
    api.scrollTo(selectedIndex)
    const handleSelect = () => setSelectedIndex(api.selectedScrollSnap())
    api.on("select", handleSelect)
    return () => { api.off("select", handleSelect) }
  }, [api, selectedIndex])

  // Single image — no carousel needed
  if (!hasMultiple) {
    return (
      <div className={cn(
        "rounded-2xl p-1.5",
        "bg-foreground/[0.02] ring-1 ring-foreground/5",
        "dark:bg-background/[0.06] dark:ring-foreground/10"
      )}>
        <div className="relative aspect-square overflow-hidden rounded-[calc(1rem)] bg-white dark:bg-white/95">
          <Image
            src={images[0]}
            alt={productName}
            fill
            className="object-contain p-8"
            // Cap effective render width at 800px on desktop. Product
            // images are decorative on a 50vw column — quality={60}
            // keeps the visual indistinguishable from q=75 while
            // dropping the bytes under the 200KB squirrelscan
            // threshold even when the crawler refuses AVIF/WebP and
            // the optimizer falls back to JPEG.
            sizes="(max-width: 1024px) 100vw, 600px"
            quality={60}
            priority
          />
        </div>
      </div>
    )
  }

  // Multiple images — use carousel
  return (
    <div>
      <div className={cn(
        "rounded-2xl p-1.5",
        "bg-foreground/[0.02] ring-1 ring-foreground/5",
        "dark:bg-background/[0.06] dark:ring-foreground/10"
      )}>
        <Carousel setApi={setApi} className="w-full">
          <CarouselContent className="-ml-0">
            {images.map((src, i) => (
              <CarouselItem key={i} className="pl-0">
                <div className="relative aspect-square overflow-hidden rounded-[calc(1rem)] bg-muted">
                  <Image
                    src={src}
                    alt={`${productName} — Image ${i + 1}`}
                    fill
                    className="object-contain p-6"
                    sizes="(max-width: 1024px) 100vw, 600px"
                    quality={60}
                    priority={i === 0}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="-left-4" />
          <CarouselNext className="-right-4" />
        </Carousel>
      </div>

      {/* Thumbnails */}
      <div className="mt-4 flex flex-wrap gap-3">
        {images.map((src, i) => (
          <button
            key={i}
            onClick={() => setSelectedIndex(i)}
            className={cn(
              "relative size-16 overflow-hidden rounded-lg ring-2 ring-offset-2 ring-offset-background transition-[transform,opacity,box-shadow,ring-color,border-color,background-color,color]",
              selectedIndex === i
                ? "ring-foreground"
                : "ring-transparent hover:ring-muted-foreground/50"
            )}
          >
            <Image
              src={src}
              alt={`Miniature ${i + 1}`}
              fill
              className="object-contain p-1"
              sizes="64px"
            />
          </button>
        ))}
      </div>
    </div>
  )
}
